import React, { memo, useMemo, useEffect, useState } from 'react';
import { H3, Radio } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import DataTime from '@gp/components/DataTime';
import { FILE_TYPES2, FILE_TYPES_MAP } from '@gp/constants/const';
import Table from './components/Table';
import { useRequest } from 'ahooks';
import { fetchStudioOverallProjectsLiveInternalBreakdownData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import {
  queryBreakdownIntelligenceList,
  queryBreakdownIntelligenceDownLoad,
  queryBreakdownInternalDownLoad,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import {
  getDownLoadCSVResponseHeaderFileName,
  downloadFileByBlob,
  stream2Object,
} from '@gp/utils/common';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { PRECONDITION } from '../../constants';
import { SORT_REQ_MAP } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects/components/Live/components/BreakdownList/constants';
import classNames from 'classnames';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import TriggerFoldBtn from '@/pages/globalpedia/components/TriggerFoldBtn';
import { toProjectDetailsByGameType } from '@gp/utils/project';
import useProjects from '@gp/pages/Pipeline/ProjectDetails/_hooks/useProjects';

interface BreakdownPropsType {
  className?: string;
  style?: React.CSSProperties;
  studioId: string;
  formModel: IStudioOverallProjectsDetailsLiveFilter;
  selectedProjects: IStudioOverallProject[];
  projectFilterLoading: boolean;
}

const DEFAULT_PROPS: BreakdownPropsType = {
  className: '',
  style: {},
  studioId: '',
  formModel: {
    projects: [],
    source: '',
  },
  selectedProjects: [],
  projectFilterLoading: true,
};

function Breakdown(props: BreakdownPropsType) {
  const { className, style, studioId, formModel, selectedProjects, projectFilterLoading } = props;

  const [downloadSortsParams, setDownLoadSortsParams] = useState<SortBy<any>>({
    by: 'total_revenue',
    order: 'desc',
  });
  const [tableIsNoData, setTableIsNoData] = useState<boolean>(true);
  const [downloadFileType, setDownloadFileType] = useState<number>(2);
  const { projects } = useProjects();
  const {
    loading: intelligenceDataLoading,
    data: intelligenceData,
    run: runIntelligence,
  } = useRequest(queryBreakdownIntelligenceList, { manual: true });
  const {
    loading: internalDataLoading,
    data: internalData,
    run: runInternal,
  } = useRequest(fetchStudioOverallProjectsLiveInternalBreakdownData, {
    manual: true,
  });
  const {
    loading: intelligenceDownloadLoading,
    data: intelligenceDownloadData,
    run: runIntelligenceDownload,
  } = useRequest(queryBreakdownIntelligenceDownLoad, { manual: true });
  const {
    loading: internalDownloadLoading,
    data: internalDownloadData,
    run: runInternalDownload,
  } = useRequest(queryBreakdownInternalDownLoad, { manual: true });

  const isSelectInternal = useMemo(() => formModel?.source == PRECONDITION, [formModel?.source]);

  const idsParams = useMemo(() => {
    if (!Array.isArray(selectedProjects) || !selectedProjects?.length) return null;
    const ids =
      selectedProjects?.map(
        (t: { id: string; name: string; platform_id: string[] | undefined; }) => {
          return {
            id: t?.id,
            platform_id: t?.platform_id,
          };
        },
      ) || [];

    return ids;
  }, [selectedProjects]);

  const internalDataTime = useMemo<string>(() => {
    if (internalDataLoading) return '';
    return internalData?.header_date || '';
  }, [internalDataLoading, internalData?.header_date]);

  const intelligenceDataTime = useMemo<string>(() => {
    if (intelligenceDataLoading) return '';
    return (intelligenceData as any)?.header_date || '';
  }, [intelligenceDataLoading, (intelligenceData as any)?.header_date]);

  const dataTime = useMemo<string>(() => {
    return isSelectInternal ? internalDataTime : intelligenceDataTime;
  }, [isSelectInternal, internalDataTime, intelligenceDataTime]);

  const handleDownload = (payload: any) => {
    if (projectFilterLoading || !idsParams) return;
    if (isSelectInternal) {
      runInternalDownload({
        order: downloadSortsParams?.order as 'asc' | 'desc',
        order_metric: SORT_REQ_MAP?.[downloadSortsParams?.by],
        select_id: idsParams?.map((t) => t?.id),
        yoy: true,
        mom: true,
        studio_id: studioId,
        file_type: payload?.fileType === FILE_TYPES_MAP.CSV ? 1 : 2,
      });
    } else {
      runIntelligenceDownload({
        projects_breakdown: {
          studio_id: studioId,
          ids: idsParams,
          order: downloadSortsParams?.order as 'asc' | 'desc',
          order_metric: downloadSortsParams?.by,
        },
        file_type: payload?.fileType === FILE_TYPES_MAP.CSV ? 1 : 2,
      });
    }
    setDownloadFileType(payload?.fileType || 2);
  };

  // 抛出下载后的文件
  const throwDownLoadResult = async () => {
    try {
      // 拿到响应头中的文件名称
      const fileName = getDownLoadCSVResponseHeaderFileName(
        isSelectInternal ? internalDownloadData?.headers : intelligenceDownloadData?.headers,
      );
      downloadFileByBlob(
        isSelectInternal ? internalDownloadData?.data : intelligenceDownloadData?.data,
        fileName,
      );
      reportOperationLog(
        ALL_REPORT_OPERATION_LOG[
          downloadFileType == FILE_TYPES_MAP.CSV
            ? 'STUDIO_OVERALL_DETAIL_PROJECTS_BREAKDOWN_CSV_SUBMIT'
            : 'STUDIO_OVERALL_DETAIL_PROJECTS_BREAKDOWN_EXCEL_SUBMIT'
        ],
        'button',
      );
    } catch (err: any) {
      const resData = await stream2Object(err?.data);
      throw new Error(resData?.msg || '');
    }
  };
  // 下载结果
  useEffect(() => {
    if (
      intelligenceDownloadLoading ||
      (!intelligenceDownloadData && !internalDownloadData) ||
      internalDownloadLoading
    )
      return;
    throwDownLoadResult();
  }, [intelligenceDownloadLoading, internalDownloadLoading]);

  // 筛选器变化调接口, 排序发生改变调用接口
  useEffect(() => {
    if (projectFilterLoading || !idsParams) return;
    if (isSelectInternal) {
      runInternal({
        order: downloadSortsParams?.order as 'asc' | 'desc',
        order_metric: SORT_REQ_MAP?.[downloadSortsParams?.by],
        select_id: idsParams?.map((t) => t?.id),
        yoy: true,
        mom: true,
        studio_id: studioId,
      });
    } else {
      runIntelligence({
        studio_id: studioId,
        ids: idsParams,
        order: downloadSortsParams?.order as 'asc' | 'desc',
        order_metric: downloadSortsParams?.by,
      });
    }
  }, [
    studioId,
    idsParams,
    downloadSortsParams?.by,
    downloadSortsParams?.order,
    projectFilterLoading,
  ]);
  const [triggerFoldExpand, setTriggerFoldExpand] = useState<boolean>(false);
  const [dataSegment, setDataSegment] = useState<'filter' | 'all'>('filter');
  // const tabs = useMemo(() => {
  //   return [
  //     {
  //       id: 'download',
  //       label: (
  //         <GeneralDownloadBtn
  //           style={{display: tableIsNoData ? 'none' : ''}}
  //           uiType="2"
  //           fileTypes={FILE_TYPES2}
  //           isLoading={intelligenceDownloadLoading || internalDownloadLoading}
  //           onConfirm={handleDownload}
  //         />
  //       ),
  //       component: (
  //         <Table
  //           loading={intelligenceDataLoading || internalDataLoading}
  //           isHideExpandButton={isSelectInternal}
  //           data={!idsParams ? null : (isSelectInternal ? internalData : intelligenceData)}
  //           setDownLoadSortsParams={setDownLoadSortsParams}
  //           setTableIsNoData={setTableIsNoData}
  //         />
  //       ),
  //     },
  //   ];
  // }, [
  //   intelligenceDownloadLoading,
  //   internalDownloadLoading,
  //   intelligenceDataLoading,
  //   internalDataLoading,
  //   idsParams,
  //   tableIsNoData,
  // ]);

  const toDetails = (cvm: any, ...restParams: any) => {
    const { game_id: projectId, game_name: project, platform } = cvm || {};
    const params = {
      id: projectId as any,
      name: project,
      tab: 'gameDetail',
      platform,
    };
    return toProjectDetailsByGameType(params, ...restParams, projects);
  };

  return (
    <CardSlot
      className={classNames({
        'dbg-studio-overall-details-projects-live-breakdown': true,
        [className as string]: !!className,
      })}
      style={style}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Breakdown
          {!!dataTime && idsParams && (
            <DataTime
              style={{ marginLeft: 6 }}
              dateType="Month"
              // eslint-disable-next-line no-nested-ternary
              date={dataTime}
            />
          )}
        </div>
      }
      loading={false}
      isNoData={false}
      rightDom={
        <div className="dbg-breakdown-list-filter-box">
          <div className="dbg-breakdown-list-filter">
            {!isSelectInternal && (
              <div className="header-fold-btn">
                <TriggerFoldBtn
                  contentText="Expand by platform"
                  foldBubble="Expand by platform"
                  onClick={() => setTriggerFoldExpand(!triggerFoldExpand)}
                />
              </div>
            )}
            <Radio.Group
              className="dbg-breakdown-list-filter-radio"
              value={dataSegment}
              onChange={(v: string) => {
                setDataSegment(v as 'filter' | 'all');
              }}
            >
              <Radio name="filter">Display available data only</Radio>
              <Radio name="all">Display all data</Radio>
            </Radio.Group>
          </div>
          <GeneralDownloadBtn
            style={{ display: tableIsNoData ? 'none' : '' }}
            uiType="2"
            fileTypes={FILE_TYPES2}
            isLoading={intelligenceDownloadLoading || internalDownloadLoading}
            onConfirm={handleDownload}
          />
        </div>
      }
    >
      <Table
        triggerFoldExpand={triggerFoldExpand}
        loading={intelligenceDataLoading || internalDataLoading}
        isHideExpandButton={isSelectInternal}
        data={!idsParams ? null : isSelectInternal ? internalData : intelligenceData}
        setDownLoadSortsParams={setDownLoadSortsParams}
        setTableIsNoData={setTableIsNoData}
        dataSegment={dataSegment}
        toDetails={toDetails}
        isNeedData={true}
        projects={projects}
      />
    </CardSlot>
  );
}

Breakdown.defaultProps = DEFAULT_PROPS;

export default memo(Breakdown);
